import { useEffect, useRef, useState } from "react";
import { getCompetitionBadges } from "@/features/badges/api";
import type { BadgeDefinition } from "@/types";

export function BadgesSection({
  competitionId,
  currentUserId,
  currentSteps = 0,
  _refreshKey = 0,
}: {
  competitionId: string;
  currentUserId: string;
  currentSteps?: number;
  refreshKey?: number;
  _refreshKey?: number;
}) {
  const [badges, setBadges] = useState<BadgeDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBadge, setSelectedBadge] = useState<BadgeDefinition | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getCompetitionBadges(competitionId, currentUserId);
        if (data) setBadges(data);
      } catch {
        setBadges([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [competitionId, currentUserId]);

  useEffect(() => {
    if (selectedBadge) {
      previousFocusRef.current = document.activeElement as HTMLElement;
    } else if (previousFocusRef.current) {
      previousFocusRef.current.focus();
      previousFocusRef.current = null;
    }
  }, [selectedBadge]);

  const earned = badges.filter((b) => b.earned);

  if (loading) {
    return (
      <div className="flex flex-col gap-3">
        <h3 className="text-text text-lg font-extrabold tracking-tight">Badges</h3>
        <div className="animate-pulse grid grid-cols-3 sm:grid-cols-5 gap-2">
          {Array.from({ length: 6 }, (_, i) => i).map((n) => (
            <div key={n} className="bg-[rgba(28,27,46,0.08)] rounded-xl aspect-square" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-row items-center justify-between">
        <h3 className="text-text text-lg font-extrabold tracking-tight">Badges</h3>
        <span className="text-text-dim text-xs font-bold">
          {earned.length}/{badges.length}
        </span>
      </div>

      {badges.length === 0 && (
        <p className="text-text-muted text-sm italic text-center py-4">
          No badges configured for this competition.
        </p>
      )}

      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
        {badges.map((badge) => {
          return (
            <button
              type="button"
              key={badge.badgeId}
              onClick={() => setSelectedBadge(badge)}
              className={`flex flex-col items-center gap-1 p-2 rounded-xl border cursor-pointer text-left font-inherit ${
                badge.earned
                  ? "bg-[rgba(32,191,107,0.08)] border-[rgba(32,191,107,0.25)]"
                  : "bg-white border-[rgba(28,27,46,0.1)] opacity-60"
              }`}
            >
              <span className="text-2xl">{badge.iconEmoji}</span>
              <span className="text-[0.625rem] font-bold text-center leading-tight text-text">
                {badge.name}
              </span>
              {badge.earned && <span className="text-[0.625rem] font-bold text-lime">Earned</span>}
            </button>
          );
        })}
      </div>

      {selectedBadge && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-[rgba(0,0,0,0.5)]"
          onClick={() => setSelectedBadge(null)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setSelectedBadge(null);
          }}
          role="none"
          tabIndex={-1}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={selectedBadge.name}
            ref={dialogRef}
            className="bg-card rounded-[20px] p-6 max-w-sm w-[90vw] border-2 border-text shadow-[2px_2px_0px_0px_#1c1b2e] flex flex-col items-center gap-4"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Escape") {
                setSelectedBadge(null);
                return;
              }
              if (e.key === "Tab") {
                const dialog = dialogRef.current;
                if (!dialog) return;
                const focusable = dialog.querySelectorAll<HTMLElement>(
                  'button:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])',
                );
                if (focusable.length === 0) {
                  e.preventDefault();
                  return;
                }
                const first = focusable[0];
                const last = focusable[focusable.length - 1];
                if (e.shiftKey) {
                  if (document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                  }
                } else if (document.activeElement === last) {
                  e.preventDefault();
                  first.focus();
                }
              }
            }}
          >
            <div className="text-5xl">{selectedBadge.iconEmoji}</div>
            <h3 className="text-text text-xl font-extrabold text-center">{selectedBadge.name}</h3>
            <p className="text-text-muted text-sm text-center leading-5">
              {selectedBadge.description}
            </p>
            <div className="bg-[rgba(28,27,46,0.04)] rounded-xl px-4 py-2 w-full text-center">
              <span className="text-text-dim text-xs font-bold uppercase tracking-wide">
                {selectedBadge.stepThreshold.toLocaleString()} step target
              </span>
            </div>
            {!selectedBadge.earned &&
              Math.max(0, selectedBadge.stepThreshold - currentSteps) > 0 && (
                <div className="w-full bg-[rgba(28,27,46,0.08)] rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-aqua h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.min(100, (currentSteps / selectedBadge.stepThreshold) * 100)}%`,
                    }}
                  />
                </div>
              )}
            <button
              type="button"
              onClick={() => setSelectedBadge(null)}
              className="mt-2 bg-aqua text-white rounded-2xl px-6 py-2.5 text-sm font-extrabold cursor-pointer border border-text shadow-[1px_1px_0px_0px_#1c1b2e] font-inherit"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
